<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  referral: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

const imageFailed = ref(false);

const headline = computed(
  () =>
    props.referral?.headline || t('CONVERSATION.REFERRAL_AD.DEFAULT_HEADLINE')
);

const body = computed(() => props.referral?.body || '');

const sourceUrl = computed(() => props.referral?.sourceUrl || '');

const imageUrl = computed(() =>
  imageFailed.value ? '' : props.referral?.imageUrl || ''
);

const handleImageError = () => {
  imageFailed.value = true;
};
</script>

<template>
  <article class="referral-ad">
    <img
      v-if="imageUrl"
      :src="imageUrl"
      :alt="headline"
      class="referral-ad__image skip-context-menu"
      loading="lazy"
      referrerpolicy="no-referrer"
      @error="handleImageError"
    />

    <div class="referral-ad__content">
      <div class="referral-ad__label">
        {{ t('CONVERSATION.REFERRAL_AD.LABEL') }}
      </div>

      <div class="referral-ad__headline">
        {{ headline }}
      </div>

      <p v-if="body" class="referral-ad__body">
        {{ body }}
      </p>

      <a
        v-if="sourceUrl"
        :href="sourceUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="referral-ad__link skip-context-menu"
        @click.stop
      >
        {{ t('CONVERSATION.REFERRAL_AD.VIEW') }}
      </a>
    </div>
  </article>
</template>

<style scoped>
.referral-ad {
  width: min(100%, 420px);
  margin-bottom: 4px;
  overflow: hidden;
  color: inherit;
  background: rgba(127, 127, 127, 0.06);
  border: 1px solid rgba(127, 127, 127, 0.28);
  border-radius: 12px;
}

.referral-ad__image {
  display: block;
  width: 100%;
  max-height: 180px;
  object-fit: cover;
  background: rgba(127, 127, 127, 0.08);
}

.referral-ad__content {
  padding: 10px 12px 11px;
}

.referral-ad__label {
  margin-bottom: 3px;
  font-size: 11px;
  font-weight: 600;
  line-height: 16px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  opacity: 0.62;
}

.referral-ad__headline {
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
}

.referral-ad__body {
  display: -webkit-box;
  margin: 4px 0 0;
  overflow: hidden;
  font-size: 12px;
  line-height: 17px;
  white-space: pre-line;
  opacity: 0.82;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 4;
}

.referral-ad__link {
  display: inline-block;
  margin-top: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #1f6feb;
  text-decoration: none;
}

.referral-ad__link:hover {
  text-decoration: underline;
}
</style>
